/* eslint-disable no-unused-vars */
import { useSearchParams } from "next/navigation";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import type { OptionProps } from "@/components/shared/Select/select.types";

export type UseSelectProps = {
  options: OptionProps[];
  disabled: boolean;
  queryKey: string;
  handleSelectOption?: (value: string) => void;
  handleClearOption?: () => void;
};

export const useSelect = (
  {
    options,
    disabled,
    queryKey,
    handleClearOption,
    handleSelectOption
  }: UseSelectProps,
  forwardedRef?: React.Ref<{ clear: () => void }>
) => {
  const searchParams = useSearchParams();
  const initialValue = searchParams.get(queryKey) || "";
  const [value, setValue] = useState(initialValue);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedHoverIndex, setSelectedHoverIndex] = useState<number | null>(
    null
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionsContainerRef = useRef<HTMLUListElement | null>(null);

  const inputId = `select-input-${queryKey}`;
  const listboxId = `select-options-${queryKey}`;

  const handleSelectBlur = useCallback(() => {
    setShowOptions(false);
    inputRef?.current?.blur();
  }, []);

  const handleSelectFocus = useCallback(() => {
    if (disabled) return;
    setShowOptions(true);
    setSelectedHoverIndex(null);
  }, [disabled]);

  const handleClearSelect = useCallback(() => {
    setValue("");
    setSelectedIndex(null);
    setShowOptions(false);
  }, []);

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLDivElement>,
      handleClickOption: (option: string, index: number) => void,
      optionContainerRef: RefObject<HTMLUListElement>
    ) => {
      if (e.key === "Tab") {
        handleSelectBlur();
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        handleSelectBlur();
        return;
      }

      let newHoverIndex = selectedHoverIndex;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (options.length === 0) return;
        if (newHoverIndex === null) {
          newHoverIndex = 0;
        } else if (newHoverIndex < options.length - 1) {
          newHoverIndex = newHoverIndex + 1;
        }
        setSelectedHoverIndex(newHoverIndex);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (options.length === 0) return;
        if (newHoverIndex === null) {
          newHoverIndex = options.length - 1;
        } else if (newHoverIndex > 0) {
          newHoverIndex = newHoverIndex - 1;
        }
        setSelectedHoverIndex(newHoverIndex);
      } else if (e.key === "Enter" && selectedHoverIndex !== null) {
        handleClickOption(
          options[selectedHoverIndex].label,
          selectedHoverIndex
        );
      }

      if (optionContainerRef.current && newHoverIndex !== null) {
        const selectedItem = optionContainerRef.current.children[
          newHoverIndex
        ] as HTMLElement;
        if (selectedItem) {
          const container = optionContainerRef.current;
          const containerHeight = container.clientHeight;
          const itemTop = selectedItem.offsetTop;
          const itemHeight = selectedItem.clientHeight;
          const itemBottom = itemTop + itemHeight;
          const margin = 100;

          if (itemBottom + margin > container.scrollTop + containerHeight) {
            container.scrollTo({
              top: itemBottom - containerHeight + margin,
              behavior: "smooth"
            });
          } else if (itemTop - margin < container.scrollTop) {
            container.scrollTo({
              top: itemTop - margin,
              behavior: "smooth"
            });
          }
        }
      }
    },
    [selectedHoverIndex, handleSelectBlur, options]
  );

  const handleClickOption = useCallback(
    (option: string, index: number) => {
      if (value === option) {
        setValue("");
        setSelectedIndex(null);
        handleSelectBlur();
        handleClearOption && handleClearOption();
        return;
      }
      setValue(option);
      setSelectedIndex(index);
      handleSelectBlur();
      handleSelectOption && handleSelectOption(option);
    },
    [handleClearOption, handleSelectBlur, handleSelectOption, value]
  );

  useImperativeHandle(forwardedRef, () => ({
    clear: handleClearSelect
  }));

  useEffect(() => {
    const newValue = searchParams.get(queryKey) || "";
    setValue(newValue);
  }, [searchParams, queryKey]);

  useEffect(() => {
    const newIndex = options.findIndex((option) => option.label === value);
    if (newIndex !== selectedIndex) {
      setSelectedIndex(newIndex === -1 ? null : newIndex);
    }
  }, [options, selectedIndex, value]);

  return {
    value,
    inputId,
    inputRef,
    listboxId,
    showOptions,
    selectedIndex,
    selectedHoverIndex,
    optionsContainerRef,
    setValue,
    handleKeyDown,
    handleSelectBlur,
    handleSelectFocus,
    handleClickOption
  };
};
